import React, { useState } from "react";
import { Input, Label, Button, Modal, CardBody } from "reactstrap";
import { AlertColourTypeConstant, AlertTypeConstant, PropellerTypeConstant } from "shared/constants";
import { Field, FieldArray, FieldProps, useFormikContext } from "formik";
import { getInputs } from "../EngineerReport.model";
import ToolTip from "Components/ToolTip";
import { TooltipMsg } from "Components/ToolTipMessage";
import env from "environment_system/env_system";
import ErrorTooltip from "Components/ErrorTooltip";
import { useDocumentTitle } from "Components/useDocument.hooks";
import Message from "Components/Message";
import { useSelector } from "react-redux";
import { RootState } from "index";
import { checkForNaN } from "GenericForms/Helper";
import { useEditReport } from "VesselReportingInfo/EditCompletedReports/EditReportProvider";
import { getAlertMessage } from "Alerts/AlertMessages";

interface VoyageParameterType {
    vesselEngines?: any[];
    mainEngineSetting?: any[];
    modal_backdrop: boolean;
    setmodal_backdrop: any;
    energy_modal_backdrop?: boolean;
    setAlertBool: any;
    setAlertMessage: any;
    setAlertFieldValue: any;
    setAlertFieldLabel: any;
    setAlertColourType: any;
    setAlertType: any;
    setFieldId: any;
    alertBool: boolean;
}

const VoyageParameterComponent = ({
    vesselEngines,
    mainEngineSetting,
    modal_backdrop,
    setmodal_backdrop,
    energy_modal_backdrop,
    setAlertBool,
    setAlertMessage,
    setAlertFieldValue,
    setAlertFieldLabel,
    setAlertColourType,
    setAlertType,
    setFieldId,
    alertBool
}: VoyageParameterType) => {
    /** State variables */
    useDocumentTitle("Voyage Parameter Report - ecoSAIL");
    const { discardwarning } = useEditReport();
    const { CurrentReport, ConfigurationLimits } = useSelector((state: RootState) => state.Reporting);
    const reportDistanceTravelled = checkForNaN(CurrentReport?.data?.vessel_reporting_information?.distance_travelled);
    const reportSteamingTime = checkForNaN(CurrentReport?.data?.vessel_reporting_information?.time_duration_since_last_report);
    let { Voyage_Parameter_inputs } = getInputs("voyage_parameter");
    const [modalIndex, setModalIndex] = useState(0);
    const [avgRPMRefresh, setAvgRPMRefresh] = useState(0);
    const [revolutionsRefresh, setRevolutionsRefresh] = useState(0);
    const { values, errors, handleChange, touched, handleBlur, setFieldValue }:
        { values: any, errors: any, handleChange: any, touched: any, handleBlur: any, setErrors: any, setFieldValue: any } = useFormikContext();
    /**State variables end */

    function tog_backdrop() {
        setmodal_backdrop(!modal_backdrop);
        removeBodyCss();
    }

    function removeBodyCss() {
        document.body.classList.add("no_padding");
    }

    /** Function to calculate slip */
    const calculateSlipPercentage = (
        name: any,
        value: number,
        idx: number,
        isRevolutionCounterNotFunctional: boolean
    ) => {
        let slip_per: number;
        if (
            isRevolutionCounterNotFunctional ?? values?.voyageParameter[idx].is_revolution_counter_not_functional
        ) {
            if (name === `voyageParameter.${[idx]}.avg_rpm`) {
                let slip_per =
                    (100 *
                        ((value *
                            reportSteamingTime *
                            values.voyageParameter[idx].propeller_pitch) /
                            1852 -
                            reportDistanceTravelled)) /
                    ((value *
                        reportSteamingTime *
                        values.voyageParameter[idx].propeller_pitch) /
                        1852);
                setFieldValue(
                    `voyageParameter.${idx}.slip_percentage`,
                    checkForNaN(slip_per)
                );
            } else if (name === `voyageParameter.${[idx]}.propeller_pitch`) {
                let slip_per =
                    (100 *
                        ((values.voyageParameter[idx]?.avg_rpm *
                            reportSteamingTime *
                            values.propeller_pitch) /
                            1852 -
                            reportDistanceTravelled)) /
                    ((values.voyageParameter[idx]?.avg_rpm *
                        reportSteamingTime *
                        values.propeller_pitch) /
                        1852);
                setFieldValue(
                    `voyageParameter.${idx}.slip_percentage`,
                    checkForNaN(slip_per)
                );
            }
        } else {
            if (name === `voyageParameter.${[idx]}.total_revolutions`) {
                slip_per =
                    (100 *
                        ((values.voyageParameter[idx].propeller_pitch * value) / 1852 -
                            reportDistanceTravelled)) /
                    ((values.voyageParameter[idx].propeller_pitch * value) / 1852);
                setFieldValue(
                    `voyageParameter.${idx}.slip_percentage`,
                    checkForNaN(slip_per)
                );
            } else if (name === `voyageParameter.${[idx]}.propeller_pitch`) {
                slip_per =
                    (100 *
                        ((value * values.voyageParameter[idx].total_revolutions) / 1852 -
                            reportDistanceTravelled)) /
                    ((value * values.voyageParameter[idx].total_revolutions) / 1852);
                setFieldValue(
                    `voyageParameter.${idx}.slip_percentage`,
                    checkForNaN(slip_per)
                );
            }
        }
    };

    /** Calculate average RPM */
    const calculateAvgRPM = (value: any, idx: number) => {
        let avgRpm = value / reportSteamingTime;
        setFieldValue(`voyageParameter.${idx}.avg_rpm`, checkForNaN(avgRpm));
        setAvgRPMRefresh(avgRPMRefresh + 1);
    }

    /** Calculate total revolutions */
    const calculateTotalRevolutions = (value: any, idx: number) => {
        let totalRevolutions = value * reportSteamingTime;
        setFieldValue(`voyageParameter.${idx}.total_revolutions`, checkForNaN(totalRevolutions));
        setRevolutionsRefresh(revolutionsRefresh + 1);
    }

    /** Handle changes of total revolutions & avg RPM */
    const handleVoyageParameterTextboxChanges = (
        name: string,
        e: any,
        idx: number,
    ) => {
        setFieldValue(name, e.target.value);
        const value = checkForNaN(e.target.value);
        if (name === `voyageParameter.${idx}.total_revolutions` && value) {
            calculateAvgRPM(value, idx);
        } else if (name === `voyageParameter.${idx}.avg_rpm` && value) {
            calculateTotalRevolutions(value, idx);
        }
        calculateSlipPercentage(name, value, idx, values?.voyageParameter[idx].is_revolution_counter_not_functional);
    };

    return (
        <CardBody className="p-2">
            <div className="table-responsive p-0">
                <table className="table mb-0">
                    <thead className="table-light">
                        <tr>
                            <th className="align-middle p-2 sr-no-width">#</th>
                            {Voyage_Parameter_inputs.map(
                                (input: any, index: number) => {
                                    return (
                                        <th
                                            key={index}
                                            className={`align-middle p-2 ${input.name === "machinery_name"
                                                ? "text-left"
                                                : "text-center"
                                                }`}
                                        >
                                            <div>
                                                {input.label}
                                                {Object.keys(
                                                    TooltipMsg.Voyage_Paramters.map(
                                                        (obj: any) => {
                                                            if (obj?.tooltip === true) {
                                                                return obj;
                                                            }
                                                            return null;
                                                        }
                                                    )
                                                ) &&
                                                    input.name !== "machinery_name" && (
                                                        <React.Fragment>
                                                            <i
                                                                className={`bx bx-info-circle ${input.name === "slip" ? " " : "asteric"}`}
                                                                id={`${input.name}_msg`}
                                                            />
                                                            <ToolTip
                                                                target={`${input.name}_msg`}
                                                                message={`${TooltipMsg.Voyage_Paramters.filter(
                                                                    (item: any) =>
                                                                        item.target ===
                                                                        `${input.name}_msg`
                                                                ).map((tool: any) => {
                                                                    return tool.message;
                                                                })}`}
                                                            />
                                                        </React.Fragment>
                                                    )}
                                            </div>
                                        </th>
                                    );
                                }
                            )}
                            <th className="align-middle p-2 text-center">
                                Sensor status
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <FieldArray name="voyageParameter">
                            {() => (
                                <React.Fragment>
                                    {vesselEngines.length > 0 ? (
                                        vesselEngines.map(
                                            (vesselEngine: any, idx: number) => {
                                                const mcrRpm = ConfigurationLimits?.machinery?.find(
                                                    (engine: any) => engine.vessel_machinery_id === vesselEngine.id
                                                )?.mcr_rpm || null;
                                                return (
                                                    <tr key={vesselEngine.id}>
                                                        <td className="align-middle p-2 text-center">
                                                            {idx + 1}
                                                        </td>
                                                        <td className="align-middle p-2">
                                                            {vesselEngine.vessel_machinery_name}
                                                        </td>
                                                        <td className="align-middle p-2 text-center">
                                                            <div className="d-inline-block">
                                                                <Field
                                                                    name={`voyageParameter.${idx}.set_rpm`}
                                                                >
                                                                    {({ field }: FieldProps) => (
                                                                        <React.Fragment>
                                                                            <Input
                                                                                onFocus={(e) => e.target.select()}
                                                                                type="text"
                                                                                className="form-control text-right max-width-7"
                                                                                id={`set_rpm_${idx}`}
                                                                                name={field.name}
                                                                                autoFocus={idx === 0}
                                                                                onBlur={(e: any) => {
                                                                                    handleBlur(e);
                                                                                    if (env?.alerts && (mcrRpm !== undefined && mcrRpm < e.target.value)) {
                                                                                        setAlertMessage(
                                                                                            getAlertMessage(
                                                                                                "MachineryData",
                                                                                                "set_rpm_above_mcr",
                                                                                                "Set RPM",
                                                                                                e.target.value,
                                                                                                mcrRpm
                                                                                            )
                                                                                        );
                                                                                        setAlertColourType(AlertColourTypeConstant.DANGER);
                                                                                        setAlertType(AlertTypeConstant.WITH_REASON);
                                                                                        setAlertBool(true);
                                                                                        setFieldId(`set_rpm_${idx}`)
                                                                                        setAlertFieldLabel("Set RPM");
                                                                                        setAlertFieldValue(e.target.value);
                                                                                    }
                                                                                }}
                                                                                value={field.value}
                                                                                onChange={field.onChange}
                                                                            />
                                                                            {errors?.voyageParameter &&
                                                                                touched?.voyageParameter &&
                                                                                touched?.voyageParameter[idx]
                                                                                    ?.set_rpm &&
                                                                                errors?.voyageParameter[idx]
                                                                                    ?.set_rpm &&
                                                                                env?.form_validation ===
                                                                                true && !alertBool && !modal_backdrop && !energy_modal_backdrop &&
                                                                                !discardwarning &&
                                                                                (
                                                                                    <ErrorTooltip
                                                                                        target={`set_rpm_${idx}`}
                                                                                        message={
                                                                                            errors?.voyageParameter &&
                                                                                            errors?.voyageParameter[
                                                                                                idx
                                                                                            ]?.set_rpm
                                                                                        }
                                                                                        open={
                                                                                            (errors?.voyageParameter &&
                                                                                                errors?.voyageParameter[
                                                                                                    idx
                                                                                                ]?.set_rpm) && !modal_backdrop && !energy_modal_backdrop
                                                                                                ? true
                                                                                                : false
                                                                                        }
                                                                                    />
                                                                                )}
                                                                        </React.Fragment>
                                                                    )}
                                                                </Field>
                                                            </div>
                                                        </td>
                                                        <td className="align-middle p-2 text-center">
                                                            <div className="d-inline-block">
                                                                <Field
                                                                    name={`voyageParameter.${idx}.avg_rpm`}
                                                                >
                                                                    {({ field }: FieldProps) => (
                                                                        <React.Fragment>
                                                                            <Input
                                                                                onFocus={(e) => e.target.select()}
                                                                                key={avgRPMRefresh}
                                                                                type="text"
                                                                                name={field.name}
                                                                                className="form-control text-right max-width-7"
                                                                                id={`avg_rpm_${idx}`}
                                                                                onBlur={(e: any) => {
                                                                                    handleBlur(e);
                                                                                    handleVoyageParameterTextboxChanges(field.name, e, idx);
                                                                                    if (env?.alerts && mcrRpm !== undefined && mcrRpm < e.target.value) {
                                                                                        setAlertMessage(
                                                                                            getAlertMessage(
                                                                                                "MachineryData",
                                                                                                "avg_rpm_above_mcr",
                                                                                                "Average RPM",
                                                                                                e.target.value,
                                                                                                mcrRpm
                                                                                            )
                                                                                        );
                                                                                        setAlertColourType(AlertColourTypeConstant.DANGER);
                                                                                        setAlertType(AlertTypeConstant.WITH_REASON);
                                                                                        setAlertBool(true);
                                                                                        setFieldId(`avg_rpm_${idx}`)
                                                                                        setAlertFieldLabel("Average RPM");
                                                                                        setAlertFieldValue(e.target.value);
                                                                                    }
                                                                                }}
                                                                                disabled={!values?.voyageParameter[idx]?.is_revolution_counter_not_functional}
                                                                                value={field.value ?? ""}
                                                                                onChange={field.onChange}
                                                                            />
                                                                            {
                                                                                errors?.voyageParameter &&
                                                                                touched?.voyageParameter &&
                                                                                touched?.voyageParameter[idx]
                                                                                    ?.avg_rpm &&
                                                                                errors?.voyageParameter[
                                                                                    idx
                                                                                ]?.avg_rpm &&
                                                                                env?.form_validation ===
                                                                                true &&
                                                                                !alertBool &&
                                                                                !modal_backdrop && !energy_modal_backdrop &&
                                                                                values.voyageParameter[idx].is_revolution_counter_not_functional &&
                                                                                !discardwarning &&
                                                                                (
                                                                                    <ErrorTooltip
                                                                                        target={`avg_rpm_${idx}`}
                                                                                        message={
                                                                                            errors?.voyageParameter[
                                                                                                idx
                                                                                            ]?.avg_rpm
                                                                                        }
                                                                                        open={
                                                                                            (errors?.voyageParameter[
                                                                                                idx
                                                                                            ]?.avg_rpm) && !modal_backdrop && !energy_modal_backdrop
                                                                                                ? true
                                                                                                : false
                                                                                        }
                                                                                    />
                                                                                )
                                                                            }
                                                                        </React.Fragment>
                                                                    )}
                                                                </Field>
                                                            </div>
                                                        </td>
                                                        <td className="align-middle p-2 text-center">
                                                            <div className="d-inline-block">
                                                                <Field
                                                                    name={`voyageParameter.${idx}.total_revolutions`}
                                                                >
                                                                    {({ field }: FieldProps) => (
                                                                        <Input
                                                                            onFocus={(e) => e.target.select()}
                                                                            key={revolutionsRefresh}
                                                                            type="text"
                                                                            className="form-control text-right max-width-7"
                                                                            id={`total_revolutions_${idx}`}
                                                                            name={field.name}
                                                                            onBlur={(e: any) => {
                                                                                handleBlur(e);
                                                                                handleVoyageParameterTextboxChanges(field.name, e, idx);
                                                                                const mcrCapacity = reportSteamingTime * mcrRpm;
                                                                                if (env?.alerts && parseFloat(e.target.value) > mcrCapacity) {
                                                                                    setAlertMessage(
                                                                                        getAlertMessage(
                                                                                            "MachineryData",
                                                                                            "total_revolutions_above_mcr_capacity",
                                                                                            "Total Revolutions",
                                                                                            e.target.value,
                                                                                            mcrCapacity
                                                                                        )
                                                                                    );
                                                                                    setAlertColourType(AlertColourTypeConstant.DANGER);
                                                                                    setAlertType(AlertTypeConstant.WITH_YES_NO_ACTION);
                                                                                    setAlertBool(true);
                                                                                    setFieldId(`total_revolutions_${idx}`);
                                                                                    setAlertFieldLabel("Total Revolutions");
                                                                                    setAlertFieldValue(e.target.value);
                                                                                }
                                                                            }}
                                                                            disabled={values?.voyageParameter[idx]?.is_revolution_counter_not_functional}
                                                                            value={field.value ?? ""}
                                                                            onChange={field.onChange}
                                                                        />
                                                                    )}
                                                                </Field>
                                                                {errors?.voyageParameter &&
                                                                    touched?.voyageParameter &&
                                                                    touched?.voyageParameter[idx]
                                                                        ?.total_revolutions &&
                                                                    errors?.voyageParameter[idx] &&
                                                                    env?.form_validation === true &&
                                                                    !alertBool &&
                                                                    !modal_backdrop && !energy_modal_backdrop &&
                                                                    !values.voyageParameter[idx].is_revolution_counter_not_functional &&
                                                                    !discardwarning &&
                                                                    (
                                                                        <ErrorTooltip
                                                                            target={`total_revolutions_${idx}`}
                                                                            message={
                                                                                errors?.voyageParameter[idx]
                                                                                    ?.total_revolutions
                                                                            }
                                                                            open={
                                                                                (errors?.voyageParameter[idx]
                                                                                    ?.total_revolutions) && !modal_backdrop && !energy_modal_backdrop
                                                                                    ? true
                                                                                    : false
                                                                            }
                                                                        />
                                                                    )}
                                                            </div>
                                                        </td>
                                                        <td className="align-middle p-2 text-center">
                                                            <div className="d-inline-block">
                                                                <Field name={`voyageParameter.${idx}.propeller_pitch`}>
                                                                    {({ field }: FieldProps) => (
                                                                        <Input
                                                                            onFocus={(e) => e.target.select()}
                                                                            type="text"
                                                                            className="form-control text-right max-width-7"
                                                                            id={`propeller_pitch_${idx}`}
                                                                            name={field.name}
                                                                            value={field.value ?? ""}
                                                                            onChange={field.onChange}
                                                                            onBlur={(e: any) => {
                                                                                handleBlur(e);
                                                                                handleVoyageParameterTextboxChanges(field.name, e, idx);
                                                                            }}
                                                                            disabled={
                                                                                mainEngineSetting !==
                                                                                    undefined &&
                                                                                    mainEngineSetting.filter(
                                                                                        (item: any) =>
                                                                                            item.vessel_machinery ===
                                                                                            vesselEngine.id &&
                                                                                            item.propeller_type ===
                                                                                            PropellerTypeConstant.FPP
                                                                                    ).length > 0
                                                                                    ? true
                                                                                    : false
                                                                            }
                                                                        />
                                                                    )}
                                                                </Field>
                                                            </div>
                                                            {errors?.voyageParameter &&
                                                                touched?.voyageParameter &&
                                                                touched?.voyageParameter[idx]
                                                                    ?.propeller_pitch &&
                                                                errors?.voyageParameter[idx] &&
                                                                env?.form_validation === true &&
                                                                !alertBool &&
                                                                !modal_backdrop && !energy_modal_backdrop && !discardwarning && (
                                                                    <ErrorTooltip
                                                                        target={`propeller_pitch_${idx}`}
                                                                        message={
                                                                            errors?.voyageParameter[idx]
                                                                                ?.propeller_pitch
                                                                        }
                                                                        open={
                                                                            (errors?.voyageParameter[idx]
                                                                                ?.propeller_pitch) && !modal_backdrop && !energy_modal_backdrop
                                                                                ? true
                                                                                : false
                                                                        }
                                                                    />
                                                                )}
                                                        </td>
                                                        <td className="align-middle p-2 text-center">
                                                            {reportSteamingTime === 0 ? (
                                                                <div className="text-center">
                                                                    <i
                                                                        className="mdi mdi-alert-circle-outline icon_s23"
                                                                        id="steaming_time_caution"
                                                                    />
                                                                    <ToolTip
                                                                        target="steaming_time_caution"
                                                                        message="The value will be calculated once the steaming time will be updated in 
                                                                                    the report"
                                                                    />
                                                                </div>
                                                            ) :
                                                                <p className="mb-0">
                                                                    {values?.voyageParameter[idx]?.slip_percentage}
                                                                </p>
                                                            }
                                                        </td>
                                                        <td className="p-2 align-middle text-center">
                                                            <Button
                                                                id={`sensor_${idx}`}
                                                                color={
                                                                    values &&
                                                                    values?.values?.voyageParameter &&
                                                                    values?.voyageParameter[idx]
                                                                        ?.is_revolution_counter_not_functional ===
                                                                    true &&
                                                                    "danger"
                                                                }
                                                                className={`btn ${values?.voyageParameter[idx]
                                                                    ?.is_revolution_counter_not_functional ===
                                                                    true
                                                                    ? "btn-danger"
                                                                    : "functional-status-color"
                                                                    } btn_size_8_cstm`}
                                                                onClick={() => {
                                                                    tog_backdrop();
                                                                    setModalIndex(idx);
                                                                }}
                                                                onMouseOver={() => setModalIndex(idx)}
                                                            >
                                                                {values?.voyageParameter[idx]
                                                                    ?.is_revolution_counter_not_functional ===
                                                                    false
                                                                    ? "Functional"
                                                                    : "Non-functional"}
                                                            </Button>
                                                            <ToolTip
                                                                target={`sensor_${idx}`}
                                                                message="Click here to change the status"
                                                            />
                                                            <Modal
                                                                isOpen={modal_backdrop}
                                                                toggle={() => {
                                                                    tog_backdrop();
                                                                }}
                                                                backdrop={"static"}
                                                                id={`sensor-${modalIndex}`}
                                                                size="md"
                                                            >
                                                                <div className="modal-header">
                                                                    <h5
                                                                        className="modal-title"
                                                                        id={`sensor-${modalIndex}`}
                                                                    >
                                                                        Action
                                                                    </h5>
                                                                </div>
                                                                <div className="modal-body">
                                                                    <strong>Please Note -</strong>
                                                                    <p>
                                                                        {TooltipMsg.Voyage_Paramters.filter(
                                                                            (item: any) =>
                                                                                item.target ===
                                                                                "is_revolution_msg"
                                                                        ).map((tool: any) => {
                                                                            return tool.message;
                                                                        })}
                                                                    </p>
                                                                    <div className="d-flex flex-wrap gap-2 align-items-center">
                                                                        <Label for="is_revolution_counter_not_functional">
                                                                            Revolution counter failure?
                                                                        </Label>
                                                                        <Field
                                                                            name={`voyageParameter.${modalIndex}.is_revolution_counter_not_functional`}
                                                                        >
                                                                            {({ field }: FieldProps) => (
                                                                                <div className="square-switch sqswitch d-flex align-items-center">
                                                                                    <Input
                                                                                        onFocus={(e) => e.target.select()}
                                                                                        type="checkbox"
                                                                                        switch="none"
                                                                                        name={field.name}
                                                                                        id="is_revolution_counter_not_functional"
                                                                                        checked={
                                                                                            values?.voyageParameter[
                                                                                                modalIndex
                                                                                            ]?.is_revolution_counter_not_functional
                                                                                        }
                                                                                        onChange={(e) => {
                                                                                            handleChange(e);
                                                                                            if (e.target.checked) {
                                                                                                calculateTotalRevolutions(values?.voyageParameter?.[modalIndex]?.avg_rpm, modalIndex);
                                                                                                calculateSlipPercentage(
                                                                                                    `voyageParameter.${modalIndex}.avg_rpm`,
                                                                                                    values?.voyageParameter?.[modalIndex]?.avg_rpm,
                                                                                                    modalIndex,
                                                                                                    e.target.checked
                                                                                                );
                                                                                            } else {
                                                                                                calculateAvgRPM(values?.voyageParameter?.[modalIndex]?.total_revolutions, modalIndex);
                                                                                                calculateSlipPercentage(
                                                                                                    `voyageParameter.${modalIndex}.total_revolutions`,
                                                                                                    values?.voyageParameter?.[modalIndex]?.total_revolutions,
                                                                                                    modalIndex,
                                                                                                    e.target.checked
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                    <Label
                                                                                        htmlFor="is_revolution_counter_not_functional"
                                                                                        data-on-label="Yes"
                                                                                        data-off-label="No"
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </Field>
                                                                    </div>
                                                                    <Button
                                                                        type="button"
                                                                        color="primary"
                                                                        className="btn_size_cstm justify_right"
                                                                        onClick={() => {
                                                                            setmodal_backdrop(false);
                                                                        }}
                                                                    >
                                                                        Ok
                                                                    </Button>
                                                                </div>
                                                            </Modal>
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                        )) : (
                                        <tr>
                                            <td colSpan={6} className="text-center py-3 text-muted">
                                                <Message message="No engine configured" type="danger" width="400px" />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            )}
                        </FieldArray>
                    </tbody>
                </table>
            </div>
        </CardBody>
    );
};
export default VoyageParameterComponent;
